// =============================================================================
// NOTIFICATIONBELL.JS — In-app notifications (First Look today)
// =============================================================================
// Rows are written only by database functions; this reads the signed-in
// user's own rows (RLS) and marks them read through an RPC. Realtime is off
// for this project, so it polls once a minute while the tab is visible and
// again whenever the window regains focus.
// =============================================================================
import { useState, useEffect, useCallback, useRef } from 'react'
import { fetchNotifications, markNotificationsRead } from '../lib/supabase'
import { formatWhen } from '../lib/appTime'

const POLL_MS = 60000

export default function NotificationBell({ onOpen, isMobile }) {
  const [items, setItems] = useState([])
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  const load = useCallback(async () => {
    const { data, error } = await fetchNotifications(30)
    if (!error) setItems(data || [])
  }, [])

  useEffect(() => {
    load()
    const tick = () => { if (document.visibilityState === 'visible') load() }
    const id = setInterval(tick, POLL_MS)
    window.addEventListener('focus', load)
    return () => { clearInterval(id); window.removeEventListener('focus', load) }
  }, [load])

  useEffect(() => {
    if (!open) return
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open])

  const unread = items.filter(n => !n.read_at)

  const openItem = async (n) => {
    setOpen(false)
    if (!n.read_at) {
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x))
      markNotificationsRead([n.id])
    }
    onOpen?.(n)
  }

  const markAll = async () => {
    setItems(prev => prev.map(x => x.read_at ? x : { ...x, read_at: new Date().toISOString() }))
    await markNotificationsRead(null)
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => { setOpen(o => !o); if (!open) load() }}
        aria-label={unread.length ? `Notifications, ${unread.length} unread` : 'Notifications'}
        aria-expanded={open}
        style={{
          position: 'relative', padding: '5px 9px', borderRadius: 7, border: '1px solid var(--card-border)',
          background: open ? 'rgba(249,115,22,0.12)' : 'transparent', color: unread.length ? 'var(--accent)' : 'var(--text3)',
          cursor: 'pointer', display: 'flex', alignItems: 'center',
        }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread.length > 0 && (
          <span style={{
            position: 'absolute', top: -5, right: -5, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8,
            background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>{unread.length > 9 ? '9+' : unread.length}</span>
        )}
      </button>

      {open && (
        <div className="pop-in" role="menu" style={{
          position: 'absolute', top: '115%', right: isMobile ? -60 : 0, width: isMobile ? 'calc(100vw - 24px)' : 360,
          maxWidth: 360, maxHeight: 440, overflowY: 'auto', background: 'var(--nav-bg)',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--card-border)',
          borderRadius: 12, boxShadow: 'var(--shadow-lg)', zIndex: 120, padding: 6,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px 8px' }}>
            <span style={{ fontSize: 12, fontWeight: 700 }}>Notifications</span>
            {unread.length > 0 && (
              <button onClick={markAll} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                Mark all read
              </button>
            )}
          </div>
          {items.length === 0 && <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>Nothing yet.</div>}
          {items.map(n => (
            <button key={n.id} role="menuitem" onClick={() => openItem(n)} style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '9px 10px', borderRadius: 8, border: 'none',
              background: n.read_at ? 'transparent' : 'rgba(249,115,22,0.08)', cursor: 'pointer', color: 'var(--text)',
              marginBottom: 2,
            }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                {!n.read_at && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', marginTop: 5, flexShrink: 0 }} />}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: n.read_at ? 500 : 700 }}>{n.title}</div>
                  {n.body && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{n.body}</div>}
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{n.actor_name ? `${n.actor_name} · ` : ''}{formatWhen(n.created_at)}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
